import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Container, Grid, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, makeStyles } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { gatewayOrderApi } from 'api/admin';
import { AggregatedPaymentResponse } from 'api/admin/types';
import { apiDateFormat, defaultDateFormat } from 'shared/constants/AppConst';
import { format } from 'date-fns';
import IntlMessages from '@crema/utility/IntlMessages';


const useStyles = makeStyles((theme) => ({
    root: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    filterCard: {
        marginBottom: theme.spacing(4),
    },
    summaryCard: {
        marginBottom: theme.spacing(4),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    tableContainer: {
        marginTop: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(2),
    }
}));

const AggregatedRazorpayPayments: React.FC = () => {
    const classes = useStyles();
    const [startDate, setStartDate] = useState<MaterialUiPickersDate>(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [endDate, setEndDate] = useState<MaterialUiPickersDate>(new Date());
    const [data, setData] = useState<AggregatedPaymentResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        if (!startDate) return;

        if (!gatewayOrderApi) {
            setError("API not initialized");
            return;
        }

        setLoading(true);
        setError(null);
        try {
            // Format date as YYYY-MM-DD
            const formattedStartDate = format(startDate, apiDateFormat);
            const formattedEndDateRaw = endDate ? format(endDate, apiDateFormat) : undefined;
            const formattedEndDate = formattedStartDate === formattedEndDateRaw ? undefined : formattedEndDateRaw;

            const response = await gatewayOrderApi.getAggregatedRazorpayPayments(formattedStartDate, formattedEndDate);
            setData(response.data);

        } catch (err: any) {
            console.error(err);
            setError(err?.response?.data?.detail || "Failed to fetch data");
            setData(null);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);



    return (
        <Container className={classes.root} maxWidth="lg">

            <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                    <Card className={classes.filterCard}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={6}>
                                    <KeyboardDatePicker
                                        autoOk
                                        variant="inline"
                                        inputVariant="outlined"
                                        label="Start Date"
                                        format={defaultDateFormat}
                                        value={startDate}
                                        InputAdornmentProps={{ position: "start" }}
                                        onChange={date => setStartDate(date)}
                                        fullWidth
                                        maxDate={endDate || new Date()}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <KeyboardDatePicker
                                        autoOk
                                        clearable
                                        variant="inline"
                                        inputVariant="outlined"
                                        label="End Date"
                                        format={defaultDateFormat}
                                        value={endDate}
                                        InputAdornmentProps={{ position: "start" }}
                                        onChange={date => setEndDate(date)}
                                        fullWidth
                                        minDate={startDate || undefined}
                                        maxDate={new Date()}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={fetchData}
                                        disabled={loading || !startDate}
                                        fullWidth
                                    >
                                        Refresh
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {data && (
                        <Card className={classes.summaryCard}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Grand Total
                                </Typography>
                                <Typography variant="h3">
                                    ₹{data.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </Typography>
                                {data.startDate && (
                                    <Typography variant="subtitle1">
                                        Date: {format(new Date(data.startDate), 'dd/MM/yyyy')} {data.endDate ? `- ${format(new Date(data.endDate), 'dd/MM/yyyy')}` : '(Till date)'}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Grid>

                <Grid item xs={12} md={8}>
                    {loading && <LinearProgress />}
                    {error && (
                        <Typography className={classes.error} variant="body1">
                            {error}
                        </Typography>
                    )}

                    {data && (
                        <>

                            <TableContainer component={Paper} className={classes.tableContainer}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Temple Name</TableCell>
                                            <TableCell align="right">Amount (₹)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.temples.map((temple) => (
                                            <TableRow key={temple.templeId}>
                                                <TableCell>{temple.templeName}</TableCell>
                                                <TableCell align="right">
                                                    ₹{temple.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};

export default AggregatedRazorpayPayments;
